import AppError from '@/components/AppError';
import { TappAvatar } from '@/components/ui/TappAvatar';
import { TappIcon } from '@/components/ui/TappIcon';
import { TappInputFilter } from '@/components/ui/TappInputFilter';
import { TappLink } from '@/components/ui/TappLink';
import { TappPageHeader } from '@/components/ui/TappPageHeader';
import { TappPaginator } from '@/components/ui/TappPaginator';
import { TappPillProps, TappPill } from '@/components/ui/TappPill';
import { TappSelectFilter } from '@/components/ui/TappSelectFilter';
import { useApiTable, TappTableWrapper, TappTable } from '@/components/ui/TappTable';
import { cn } from '@/components/ui/utils';
import { UserStatus, Customer, getUserDisplayName } from '@/lib/models/user';
import { ApiUtils } from '@/lib/utils/api';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import Papa from 'papaparse';
import { useAlert, useConfirm } from '@/components/ui/alert-dialog-provider';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const CustomersPage = () => {
  const { t } = useTranslation();

  return (
    <>
      <TappPageHeader title={t('customers')} backLink="/admin" />
      <Table />
    </>
  );
};

const TableFilters = ({ onExportCsv }: { onExportCsv: () => void }) => {
  const { t } = useTranslation();

  return (
    <section className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <TappInputFilter
          name="search"
          border
          placeholder={t('search')}
          prefix={<TappIcon>search</TappIcon>}
          className="min-w-[240px]"
          clearable
        />

        <TappSelectFilter
          name="status"
          placeholder={t('customerStatus')}
          options={[
            { label: t('active'), value: UserStatus.active },
            { label: t('suspended'), value: UserStatus.suspended },
            { label: t('blocked'), value: UserStatus.blocked },
          ]}
          border
          className="min-w-[180px]"
        />
      </div>

      <Button variant="outline" onClick={onExportCsv}>
        <TappIcon className="mr-2">download</TappIcon>
        {t('exportCsv')}
      </Button>
    </section>
  );
};

const Table = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const alert = useAlert();
  const queryClient = useQueryClient();

  const { mutateAsync: blockCustomer, isPending: isBlocking } = useMutation({
    mutationFn: (customerId: string) => ApiUtils.put(`users/customers/${customerId}/block`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const { mutateAsync: unblockCustomer, isPending: isUnblocking } = useMutation({
    mutationFn: (customerId: string) => ApiUtils.put(`users/customers/${customerId}/unblock`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const handleBlockClick = async (customer: Customer) => {
    const ok = await confirm({
      title: t('blockCustomer'),
      body: t('blockCustomerConfirmBody', { name: getUserDisplayName(customer) }),
      cancelButton: t('cancel'),
      actionButton: t('confirmBlock'),
    });

    if (!ok) return;

    try {
      await blockCustomer(customer._id);
    } catch (err: any) {
      if (err?.status === 409) {
        await alert({
          title: t('cannotBlockCustomer'),
          body: t('cannotBlockCustomerBody'),
        });
      } else {
        console.error(err);
      }
    }
  };

  const handleUnblockClick = async (customer: Customer) => {
    const ok = await confirm({
      title: t('unblockCustomer'),
      body: t('unblockCustomerConfirmBody', { name: getUserDisplayName(customer) }),
      cancelButton: t('cancel'),
      actionButton: t('confirmUnblock'),
    });

    if (!ok) return;

    try {
      await unblockCustomer(customer._id);
    } catch (err: any) {
      if (err?.status === 409) {
        await alert({
          title: t('cannotUnblockCustomer'),
          body: t('cannotUnblockCustomerBody'),
        });
      } else {
        console.error(err);
      }
    }
  };

  const statusColors: Record<UserStatus, TappPillProps['color']> = {
    [UserStatus.active]: 'success',
    [UserStatus.suspended]: 'destructive',
    [UserStatus.blocked]: 'destructive',
    [UserStatus.pending]: 'amber',
    [UserStatus.deleted]: 'secondary',
  };

  const columnHelper = createColumnHelper<Customer>();

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) => ({
          src: row.profilePictureUrl,
          name: getUserDisplayName(row),
          phone: row.phoneNumber,
        }),
        {
          id: 'firstName',
          header: t('name'),
          enableSorting: true,
          cell: (info) => (
            <div className="flex items-center gap-4">
              <TappAvatar {...info.getValue()} />
              <p className="text-sm">{info.getValue().name}</p>
            </div>
          ),
        }
      ),
      columnHelper.accessor('phoneNumber', {
        header: t('phone'),
        enableSorting: true,
      }),
      columnHelper.accessor('ridesCount', {
        header: t('rides'),
        enableSorting: true,
      }),
      columnHelper.accessor('rating', {
        header: t('rating'),
        enableSorting: true,
      }),
      columnHelper.accessor('status', {
        header: t('status'),
        enableSorting: true,
        cell: (info) => <TappPill color={statusColors[info.getValue()]}>{t(info.getValue())}</TappPill>,
      }),
      columnHelper.display({
        id: 'block',
        header: '',
        cell: ({ row: { original } }) =>
          original.status === UserStatus.deleted ? null : original.status === UserStatus.active ? (
            <button
              className="text-red-600 hover:underline"
              onClick={() => handleBlockClick(original)}
              disabled={isBlocking}
            >
              {t('block')}
            </button>
          ) : original.status === UserStatus.blocked ? (
            <button
              className="text-green-600 hover:underline"
              onClick={() => handleUnblockClick(original)}
              disabled={isUnblocking}
            >
              {t('activate')}
            </button>
          ) : null,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) =>
          original.status === UserStatus.deleted ? null : (
            <div className="flex items-center justify-end">
              <TappLink to={`/admin/customers/${original._id!}`}>{t('edit')}</TappLink>
            </div>
          ),
      }),
    ],
    [t, isBlocking, isUnblocking]
  );

  const { table, isLoading, total, error } = useApiTable<Customer>({
    columns,
    queryKey: (filter) => ['customers', filter],
    queryFn: (filter) =>
      ApiUtils.get('users/customers', {
        query: { ...filter },
      }),
    enableSortingRemoval: true,
  });

  const handleExportCsv = () => {
    const rows = table.getRowModel().rows.map((row) => row.original);
    const csvData = rows.map((customer) => ({
      [t('name')]: getUserDisplayName(customer),
      [t('phone')]: customer.phoneNumber,
      [t('rides')]: customer.ridesCount,
      [t('rating')]: customer.rating,
      [t('status')]: t(customer.status),
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `customers-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (error) return <AppError error={error} />;

  return (
    <>
      <TableFilters onExportCsv={handleExportCsv} />
      <TappTableWrapper className={cn({ isLoading })}>
        <TappTable table={table} stickyHeader />
        <TappPaginator table={table} total={total} />
      </TappTableWrapper>
    </>
  );
};

export default CustomersPage;
